import React from "react";
import { FormattedMessage } from "react-intl";
import _ from "lodash";

export const formatUnitValue = (value, unit, intl, digits) => (
	value === undefined
		? "---"
		: intl.formatNumber(unit.toAnzeige(value), {
			minimumFractionDigits: digits === undefined ? unit.digits : digits,
			maximumFractionDigits: digits === undefined ? unit.digits : digits
		})
);

export const unformatUnitValue = (value, unit) => (
	value === undefined
	? NaN
	: unit.fromAnzeige(value)
);

const genSingleUnit = i18n_key =>
	<FormattedMessage id={i18n_key} />;

const genSingleUnitText = (i18n_key, intl) =>
	intl.formatMessage({ id: i18n_key });

const genUnitByUnit = (a, b) =>
	<span>{ a.genUnit() }*{ b.genUnit() }</span>;

const genUnitByUnitText = (a, b, intl) =>
	a.genUnitText(intl) + b.genUnitText(intl);

const genUnitOverUnit = (dividend, divisor) => {
	let oben = dividend.need_parenthesis
		? <span>({ dividend.genUnit() })</span>
		: dividend.genUnit();
	let unten = divisor.need_parenthesis
		? <span>({ divisor.genUnit() })</span>
		: divisor.genUnit();
	return <span>{oben}/{unten}</span>;
};

const genUnitOverUnitText = (dividend, divisor, intl) => {
	let oben = dividend.genUnitText(intl);
	if (dividend.need_parenthesis) {
		oben = "(" + oben + ")";
	}
	let unten = divisor.genUnitText(intl);
	if (divisor.need_parenthesis) {
		unten = "(" + unten + ")";
	}
	return oben + "/" + unten;
};

/* **********************
 * umschaltbare Einheiten
 * **********************/

const makeSingleUnit = (relative_proportion, digits, i18n_key) => ({
	toAnzeige: value => value * relative_proportion,
	fromAnzeige: value => value * (1/relative_proportion),
	relative_proportion: relative_proportion,
	digits: digits,
	genUnit: () => genSingleUnit(i18n_key),
	genUnitText: intl => genSingleUnitText(i18n_key, intl)
});

/* ********
 * pressure
 * ********/

/* diese Einheit wird intern verwendet */
const bar = makeSingleUnit(1, 1, "bar");

/* Alternativen für "bar" */
const psi = makeSingleUnit(14.50377377, 0, "psi");
const MPa = makeSingleUnit(0.1, 2, "MPa");

/* ******
 * volume
 * ******/

/* diese Einheit wird intern verwendet */
const m3 = makeSingleUnit(1, 0, "m³");

/* Alternativen für "m3" */
const cft = makeSingleUnit(35.31467, 0, "cft");

/* ***********
 * temperature
 * ***********/

/* diese Einheit wird intern verwendet */
const degC = makeSingleUnit(1, 0, "°C");

/* Alternativen für "degC" */
const degF = {
	toAnzeige: value => value * 1.8 + 32,
	fromAnzeige: value => (value - 32) / 1.8,
	relative_proportion: 1.8,
	digits: 0,
	genUnit: () => genSingleUnit("°F"),
	genUnitText: intl => genSingleUnitText("°F", intl)
};

const degK = {
	toAnzeige: value => value + 273.15,
	fromAnzeige: value => value - 273.15,
	relative_proportion: 1,
	digits: 0,
	genUnit: () => genSingleUnit("K"),
	genUnitText: intl => genSingleUnitText("K", intl)
};

/* *******************
 * statische Einheiten
 * *******************/

const seconds = makeSingleUnit(1, 0, "s");
const minutes = makeSingleUnit(1, 0, "min");
const hours = makeSingleUnit(1, 0, "h");
const kW = makeSingleUnit(1, 1, "kW");
const percent = makeSingleUnit(100, 0, "%");
const EUR = makeSingleUnit(1, 2, "€");
const USD = makeSingleUnit(1, 2, "$");	/* TODO: alle Währungen durch ein "flexibles" Objekt ersetzen */
const kg = makeSingleUnit(1, 2, "kg");
const l = makeSingleUnit(1, 1, "l");

/* **************************
 * zusammengesetzte Einheiten
 * **************************/

const makeUnitByUnit = (a, b, digits) => {
	let rp = a.relative_proportion * b.relative_proportion;
	if (digits === undefined) {
		digits = a.digits + b.digits;
	}
	return {
		toAnzeige: value => value * rp,
		fromAnzeige: value => value * (1/rp),
		relative_proportion: rp,
		digits,
		genUnit: () => genUnitByUnit(a, b),
		genUnitText: intl => genUnitByUnitText(a, b, intl)
	};
};

const makeUnitOverUnit = (dividend, divisor, digits) => {
	let rp = dividend.relative_proportion / divisor.relative_proportion;
	if (digits === undefined) {
		digits = dividend.digits + divisor.digits;
	}
	return {
		toAnzeige: value => value * rp,
		fromAnzeige: value => value * (1/rp),
		relative_proportion: rp,
		digits,
		genUnit: () => genUnitOverUnit(dividend, divisor),
		genUnitText: intl => genUnitOverUnitText(dividend, divisor, intl),
		need_parenthesis: true
	};
};

const kWh = makeUnitByUnit(kW, hours, 1);
const m3_over_minutes = makeUnitOverUnit(m3, minutes, 2);
const m3_over_hours = makeUnitOverUnit(m3, hours, 2);
const cft_over_minutes = makeUnitOverUnit(cft, minutes, 2);
const cft_over_hours = makeUnitOverUnit(cft, hours, 2);
const kW_over_m3_over_minutes = makeUnitOverUnit(kW, m3_over_minutes, 3);
const kW_over_cft_over_minutes = makeUnitOverUnit(kW, cft_over_minutes, 3);
const EUR_over_m3 = makeUnitOverUnit(EUR, m3, 5);
const EUR_over_cft = makeUnitOverUnit(EUR, cft, 5);
const USD_over_m3 = makeUnitOverUnit(USD, m3, 5);
const USD_over_cft = makeUnitOverUnit(USD, cft, 5);
const EUR_over_kWh = makeUnitOverUnit(EUR, kWh, 2);
const USD_over_kWh = makeUnitOverUnit(USD, kWh, 2);
const EUR_over_l = makeUnitOverUnit(EUR, l, 2);
const USD_over_l = makeUnitOverUnit(USD, l, 2);
const kWh_over_l = makeUnitOverUnit(kWh, l, 2);
const kg_over_l = makeUnitOverUnit(kg, l, 2);
const kWh_over_m3 = makeUnitOverUnit(kWh, m3, 2);
const kg_over_m3 = makeUnitOverUnit(kg, m3, 2);
const kWh_over_cft = makeUnitOverUnit(kWh, cft, 2);
const kg_over_cft = makeUnitOverUnit(kg, cft, 2);
const kg_over_kWh = makeUnitOverUnit(kg, kWh, 2);

export const getUnit = name => {
	return {
		bar: bar,
		psi: psi,
		MPa: MPa,
		m3: m3,
		cft: cft,
		degC: degC,
		degF: degF,
		degK: degK,
		seconds: seconds,
		minutes: minutes,
		hours: hours,
		kW: kW,
		kWh: kWh,
		kg: kg,
		l: l,
		percent: percent,
		EUR: EUR,
		USD: USD,
		EUR_over_m3: EUR_over_m3,
		EUR_over_cft: EUR_over_cft,
		USD_over_m3: USD_over_m3,
		USD_over_cft: USD_over_cft,
		m3_over_minutes: m3_over_minutes,
		m3_over_hours: m3_over_hours,
		cft_over_minutes: cft_over_minutes,
		cft_over_hours: cft_over_hours,
		kW_over_m3_over_minutes: kW_over_m3_over_minutes,
		kW_over_cft_over_minutes: kW_over_cft_over_minutes,
		EUR_over_kWh: EUR_over_kWh,
		USD_over_kWh: USD_over_kWh,
		EUR_over_l: EUR_over_l,
		USD_over_l: USD_over_l,
		kWh_over_l: kWh_over_l,
		kg_over_l: kg_over_l,
		kWh_over_m3: kWh_over_m3,
		kg_over_m3: kg_over_m3,
		kWh_over_cft: kWh_over_cft,
		kg_over_cft: kg_over_cft,
		kg_over_kWh: kg_over_kWh
	}[name];
};

/* Der Eigenschaftsname steht für die physikalische Größe, für einige
 * Einheiten ergeben sich wählbare Alternativen, inklusive für die
 * von den Basiseinheiten abgeleiteten Werten.
 * Beispiel:
 * volume_over_minutes setzt sich bei einer Benutzereinstellung als
 * "m3 über Minuten" zusammen, in einer anderen Einstellung aber als
 * "cft über Minuten".
 * Der Wert jeder Eigenschaft kann die intern verwendete Größe für die
 * Anzeige im Display umrechnen, und gibt Hinweise zur Formatierung
 * (Anzahl der Nachkommastellen).
 * 
 * Weil im Store alles serialisierbar sein muss, hier nur die Strings
 * ablegen, und getUnit() verwenden um an die Unit-Objekte zu kommen.
 */
const default_units = {
	pressure: "bar",		/* direkt umschaltbar */
	volume: "m3",			/* direkt umschaltbar */
	temperature: "degC",	/* direkt umschaltbar */
	seconds: "seconds",		/* fix */
	minutes: "minutes",		/* fix */
	hours: "hours",			/* fix */
	currency: "EUR",		/* konfigurierbar */
	currency_over_volume: "EUR_over_m3",	/* indirekt umschaltbar */
	currency_over_energy: "EUR_over_kWh",	/* indirekt umschaltbar */
	currency_over_l: "EUR_over_l",	/* indirekt umschaltbar */
	mass: "kg",				/* fix */
	power: "kW",			/* fix */
	energy: "kWh",			/* fix */
	volume_over_minutes: "m3_over_minutes",	/* indirekt umschaltbar */
	volume_over_hours: "m3_over_hours",	/* indirekt umschaltbar */
	percent: "percent",		/* fix */
	energy_over_volume_over_minutes: "kW_over_m3_over_minutes",	/* indirekt umschaltbar */
	energy_over_volume: "kWh_over_m3",	/* indirekt umschaltbar */
	mass_over_volume: "kg_over_m3"	/* indirekt umschaltbar */
};

const UNITS_CHANGED = "UNITS_CHANGED";

export const unitsChangedAction = unit_settings => ({
	type: UNITS_CHANGED,
	units: {
		pressure: unit_settings.pressure,
		volume: unit_settings.volume,
		temperature: unit_settings.temperature,
		currency: unit_settings.currency,
		currency_over_volume: unit_settings.currency + "_over_" + unit_settings.volume,
		currency_over_energy: unit_settings.currency + "_over_kWh",
		currency_over_l: unit_settings.currency + "_over_l",
		volume_over_minutes: unit_settings.volume + "_over_minutes",
		volume_over_hours: unit_settings.volume + "_over_hours",
		energy_over_volume_over_minutes: "kW_over_" + unit_settings.volume + "_over_minutes",
		energy_over_volume: "kWh_over_" + unit_settings.volume,
		mass_over_volume: "kg_over_" + unit_settings.volume
	}
});

export const unitsReducer = (state = default_units, action) => {
	switch (action.type) {
	case UNITS_CHANGED: {
		let new_state = _.merge({}, state, action.units);
		return new_state;
	}
	default:
		break;
	}
	return state;
};
